"use client";

import { useRef, useState, type FormEvent } from "react";
import { Button, Input } from "./ui";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Eye,
  EyeOff,
  Mail,
} from "lucide-react";
import { platform, type DesignSession } from "./api";
import styles from "./ManagedAuth.module.css";
import { BUILDY } from "./brand";

export function ManagedAuth({
  onLogin,
}: {
  onLogin: (session: DesignSession) => void;
}) {
  const [mode, setMode] = useState<"login" | "recover" | "access">(() =>
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("recover") === "1"
      ? "recover"
      : "login",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  function changeMode(next: typeof mode) {
    setMode(next);
    setError("");
    setPassword("");
    setVisible(false);
    setSent(false);
    requestAnimationFrame(() => heading.current?.focus());
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (mode === "recover") {
        await platform("auth/recover_password/", {
          method: "POST",
          body: { email: email.trim() },
        });
        setSent(true);
        requestAnimationFrame(() => heading.current?.focus());
      } else {
        const bytes = new TextEncoder().encode(email.trim() + ":" + password);
        const authorization =
          "Basic " +
          btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""));
        onLogin(
          await platform<DesignSession>("design/login/", {
            method: "POST",
            body: {},
            authorization,
          }),
        );
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "We couldn’t connect. Please try again.",
      );
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  const title = sent
    ? "Check your inbox."
    : mode === "recover"
      ? "Reset your password."
      : mode === "access"
        ? "A little early. A lot to come."
        : "Sign in to Buildy.";
  const description = sent
    ? "If an account exists for this email, you’ll receive a secure password reset link."
    : mode === "recover"
      ? "Enter your account email and we’ll send you a secure reset link."
      : mode === "access"
        ? "Buildy is currently available by invitation. Public sign-up isn’t open yet."
        : "Use your Intelliminds or Listy account to continue.";

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <a className={styles.brand} href="/" aria-label="Buildy home">
          <img
            className={styles.brandRobot}
            src={BUILDY.robot}
            alt=""
            width={37}
            height={44}
          />
          <span>{BUILDY.name}</span>
        </a>
        <span className={styles.pilot}>
          <span />
          Private preview
        </span>
      </header>
      <main className={styles.main}>
        <section
          className={styles.card}
          aria-labelledby="design-auth-title"
          aria-busy={busy}
        >
          <p className={styles.eyebrow}>
            {sent
              ? "Email on its way"
              : mode === "login"
                ? "Welcome back"
                : mode === "recover"
                  ? "Account access"
                  : "Made for your next chapter"}
          </p>
          {sent && (
            <span className={styles.successIcon} aria-hidden="true">
              <Mail size={24} />
            </span>
          )}
          <h1 id="design-auth-title" ref={heading} tabIndex={-1}>
            {title}
          </h1>
          <p className={styles.lede}>{description}</p>

          {mode !== "access" && !sent && (
            <form
              className={styles.form}
              onSubmit={(event) => void submit(event)}
            >
              <div className={styles.field}>
                <label htmlFor="design-email">Email address</label>
                <Input
                  id="design-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={busy}
                  value={email}
                  data-private
                  placeholder="you@company.com"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError("");
                  }}
                />
              </div>
              {mode === "login" && (
                <div className={styles.field}>
                  <div className={styles.labelRow}>
                    <label htmlFor="design-password">Password</label>
                    <Button
                      variant="ghost"
                      className={styles.textButton}
                      disabled={busy}
                      onClick={() => changeMode("recover")}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <div className={styles.password}>
                    <Input
                      id="design-password"
                      name="password"
                      type={visible ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      disabled={busy}
                      value={password}
                      data-private
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setError("");
                      }}
                    />
                    <Button
                      variant="ghost"
                      className={styles.reveal}
                      disabled={busy}
                      aria-label={visible ? "Hide password" : "Show password"}
                      aria-pressed={visible}
                      onClick={() => setVisible((value) => !value)}
                    >
                      {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </div>
              )}
              {error && (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className={styles.submit} disabled={busy}>
                {busy
                  ? mode === "recover"
                    ? "Sending reset link…"
                    : "Signing in…"
                  : mode === "recover"
                    ? "Send reset link"
                    : "Sign in"}
                {!busy && <ArrowUpRight size={18} aria-hidden="true" />}
              </Button>
            </form>
          )}
          {sent && (
            <p className={styles.success} role="status">
              <Check size={18} aria-hidden="true" />
              Check your spam folder if it doesn’t arrive.
            </p>
          )}
          {mode === "login" ? (
            <div className={styles.accountNote}>
              <p>New to Buildy?</p>
              <Button
                variant="ghost"
                className={styles.textButton}
                onClick={() => changeMode("access")}
                disabled={busy}
              >
                About early access <ArrowUpRight size={14} aria-hidden="true" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              className={styles.back}
              disabled={busy}
              onClick={() => changeMode("login")}
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Back to sign in
            </Button>
          )}
        </section>
        <p className={styles.caption}>
          One account for Intelliminds, Listy, and Buildy.
        </p>
      </main>
      <footer className={styles.footer}>
        Your website. A little more you.
      </footer>
    </div>
  );
}
